import React from 'react'
import { useCurrentPractice } from '../contexts/PracticeContext'
import LoadingSpinner from './LoadingSpinner'
import { useRefundsPermission } from '../hooks/useRefundsPermission'
import { selectContext } from 'selectors/auth'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { HighlightOff } from '@mui/icons-material'
import { AlertModal } from './common/AlertModal'

type RefundsGuardProps = {
	children: React.ReactNode
	onRestrictModalClose?: () => void
}

/**
 * Guard component to enable/disable refund component.
 *
 * Usage:
 *   <RefundsGuard>
 *     <RefundsPage />
 *   </RefundsGuard>
 *
 * If refunds are enabled for staff, children are rendered, otherwise, they are hidden.
 */
const RefundsGuard: React.FC<RefundsGuardProps> = ({ children }) => {
	const { currentPracticeObject } = useCurrentPractice()
	const context: Context = useSelector(selectContext)
	const navigate = useNavigate()
	const currentUser = context?.user as any
	const { disableRefundsForStaff } = useRefundsPermission()

	const handleCloseModal = () => {
		navigate('/dashboard')
	}

	if (!currentPracticeObject?.id || !currentUser) {
		return <LoadingSpinner />
	}

	if (disableRefundsForStaff) {
		return (
			<AlertModal
				icon={<HighlightOff color="error" sx={{ fontSize: 50 }} />}
				title="Oops!"
				text="You do not have permissions to process a payment or refund a payment outside of
					your Practice Management System. Please proceed in your Practice Management System."
				buttonSx={{ color: 'white', borderRadius: '5px !important' }}
				buttonProps={{ variant: 'contained', color: 'error', size: 'medium' }}
				isOpen={true}
				onClose={() => handleCloseModal()}
			/>
		)
	}

	return <>{children}</>
}

export default RefundsGuard
